import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Calculator, Zap, CheckCircle2 } from 'lucide-react';

interface CalculationStats {
  calculationsPerformed: number;
  totalTime: number;
  averageTime: number;
  lastOperation: string;
  errorsEncountered: number;
}

interface CalculationResult {
  expression: string;
  result: number | string;
  isError: boolean;
  processingTime: number;
  timestamp: Date;
}

const ScientificCalculatorTool: React.FC = () => {
  const [display, setDisplay] = useState<string>('0');
  const [expression, setExpression] = useState<string>('');
  const [memory, setMemory] = useState<number>(0);
  const [angleMode, setAngleMode] = useState<'deg' | 'rad'>('deg');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<CalculationStats>({
    calculationsPerformed: 0,
    totalTime: 0,
    averageTime: 0,
    lastOperation: '',
    errorsEncountered: 0
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<CalculationResult[]>([]);
  const [lastResult, setLastResult] = useState<number>(0);

  const toRadians = (degrees: number): number => degrees * (Math.PI / 180);
  const toDegrees = (radians: number): number => radians * (180 / Math.PI);

  const formatNumber = (num: number): string => {
    if (Math.abs(num) >= 1e10 || (Math.abs(num) < 1e-6 && num !== 0)) {
      return num.toExponential(6);
    }
    return parseFloat(num.toFixed(10)).toString();
  };

  const evaluateExpression = useCallback((expr: string): CalculationResult => {
    const startTime = performance.now();
    let result: number | string;
    let isError = false;

    try {
      // Replace mathematical functions and constants
      let processedExpr = expr
        .replace(/π/g, Math.PI.toString())
        .replace(/e/g, Math.E.toString())
        .replace(/sin\(/g, angleMode === 'deg' ? 'Math.sin(toRadians(' : 'Math.sin(')
        .replace(/cos\(/g, angleMode === 'deg' ? 'Math.cos(toRadians(' : 'Math.cos(')
        .replace(/tan\(/g, angleMode === 'deg' ? 'Math.tan(toRadians(' : 'Math.tan(')
        .replace(/asin\(/g, angleMode === 'deg' ? 'toDegrees(Math.asin(' : 'Math.asin(')
        .replace(/acos\(/g, angleMode === 'deg' ? 'toDegrees(Math.acos(' : 'Math.acos(')
        .replace(/atan\(/g, angleMode === 'deg' ? 'toDegrees(Math.atan(' : 'Math.atan(')
        .replace(/ln\(/g, 'Math.log(')
        .replace(/log\(/g, 'Math.log10(')
        .replace(/sqrt\(/g, 'Math.sqrt(')
        .replace(/cbrt\(/g, 'Math.cbrt(')
        .replace(/abs\(/g, 'Math.abs(')
        .replace(/floor\(/g, 'Math.floor(')
        .replace(/ceil\(/g, 'Math.ceil(')
        .replace(/round\(/g, 'Math.round(')
        .replace(/exp\(/g, 'Math.exp(')
        .replace(/\^/g, '**')
        .replace(/×/g, '*')
        .replace(/÷/g, '/');

      // Add closing parentheses for angle functions if needed
      if (angleMode === 'deg') {
        processedExpr = processedExpr
          .replace(/Math\.sin\(toRadians\(/g, 'Math.sin(toRadians(')
          .replace(/Math\.cos\(toRadians\(/g, 'Math.cos(toRadians(')
          .replace(/Math\.tan\(toRadians\(/g, 'Math.tan(toRadians(');
      }

      // Create a safe evaluation function
      const safeEval = new Function('Math', 'toRadians', 'toDegrees', 'return ' + processedExpr);
      const numResult = safeEval(Math, toRadians, toDegrees);

      if (typeof numResult === 'number' && isFinite(numResult)) {
        result = numResult;
      } else if (typeof numResult === 'number' && !isFinite(numResult)) {
        result = numResult === Infinity ? 'Infinity' : numResult === -Infinity ? '-Infinity' : 'NaN';
        isError = true;
      } else {
        result = 'Error';
        isError = true;
      }
    } catch (error) {
      result = 'Error';
      isError = true;
    }

    const endTime = performance.now();
    return {
      expression: expr,
      result,
      isError,
      processingTime: endTime - startTime,
      timestamp: new Date()
    };
  }, [angleMode, toRadians, toDegrees]);

  const calculate = useCallback((): void => {
    if (!expression) return;

    const calculationResult = evaluateExpression(expression);
    
    setHistory(prev => [calculationResult, ...prev.slice(0, 19)]); // Keep last 20 calculations
    
    if (!calculationResult.isError && typeof calculationResult.result === 'number') {
      setDisplay(formatNumber(calculationResult.result));
      setLastResult(calculationResult.result);
    } else {
      setDisplay(calculationResult.result.toString());
    }

    setStats(prev => ({
      calculationsPerformed: prev.calculationsPerformed + 1,
      totalTime: prev.totalTime + calculationResult.processingTime,
      averageTime: (prev.totalTime + calculationResult.processingTime) / (prev.calculationsPerformed + 1),
      lastOperation: expression,
      errorsEncountered: prev.errorsEncountered + (calculationResult.isError ? 1 : 0)
    }));

    setExpression('');
  }, [expression, evaluateExpression]);

  const handleButtonClick = (value: string): void => {
    switch (value) {
      case 'C':
        setDisplay('0');
        setExpression('');
        break;
      case 'CE':
        setDisplay('0');
        break;
      case '=':
        calculate();
        break;
      case '±':
        if (display !== '0') {
          const newDisplay = display.startsWith('-') ? display.slice(1) : '-' + display;
          setDisplay(newDisplay);
          setExpression(prev => prev + newDisplay);
        }
        break;
      case '√':
        setExpression(prev => prev + 'sqrt(');
        setDisplay('sqrt(');
        break;
      case 'x²':
        setExpression(prev => prev + '^2');
        setDisplay(display + '²');
        break;
      case 'x³':
        setExpression(prev => prev + '^3');
        setDisplay(display + '³');
        break;
      case 'xʸ':
        setExpression(prev => prev + '^');
        setDisplay(display + '^');
        break;
      case '1/x':
        setExpression(prev => prev + '(1/');
        setDisplay('1/(');
        break;
      case 'log':
        setExpression(prev => prev + 'log(');
        setDisplay('log(');
        break;
      case 'ln':
        setExpression(prev => prev + 'ln(');
        setDisplay('ln(');
        break;
      case 'sin':
        setExpression(prev => prev + 'sin(');
        setDisplay('sin(');
        break;
      case 'cos':
        setExpression(prev => prev + 'cos(');
        setDisplay('cos(');
        break;
      case 'tan':
        setExpression(prev => prev + 'tan(');
        setDisplay('tan(');
        break;
      case 'asin':
        setExpression(prev => prev + 'asin(');
        setDisplay('asin(');
        break;
      case 'acos':
        setExpression(prev => prev + 'acos(');
        setDisplay('acos(');
        break;
      case 'atan':
        setExpression(prev => prev + 'atan(');
        setDisplay('atan(');
        break;
      case 'π':
        setExpression(prev => prev + 'π');
        setDisplay('π');
        break;
      case 'e':
        setExpression(prev => prev + 'e');
        setDisplay('e');
        break;
      case 'MC':
        setMemory(0);
        showNotification('Memory cleared');
        break;
      case 'MR':
        setDisplay(formatNumber(memory));
        setExpression(prev => prev + memory.toString());
        break;
      case 'MS':
        setMemory(parseFloat(display) || 0);
        showNotification('Memory stored');
        break;
      case 'M+':
        setMemory(prev => prev + (parseFloat(display) || 0));
        showNotification('Added to memory');
        break;
      case 'M-':
        setMemory(prev => prev - (parseFloat(display) || 0));
        showNotification('Subtracted from memory');
        break;
      case 'DEG':
      case 'RAD':
        setAngleMode(angleMode === 'deg' ? 'rad' : 'deg');
        showNotification(`Switched to ${angleMode === 'deg' ? 'radians' : 'degrees'}`);
        break;
      case '⌫':
        if (expression.length > 0) {
          setExpression(prev => prev.slice(0, -1));
          setDisplay(prev => prev.length > 1 ? prev.slice(0, -1) : '0');
        }
        break;
      default:
        if (display === '0' && !isNaN(Number(value))) {
          setDisplay(value);
          setExpression(value);
        } else {
          setDisplay(prev => prev + value);
          setExpression(prev => prev + value);
        }
        break;
    }
  };

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(display);
      showNotification('Result copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadHistory = (): void => {
    const historyContent = `Scientific Calculator History
Generated: ${new Date().toLocaleString()}

Recent Calculations:
${history.map((calc, index) => 
  `${index + 1}. ${calc.expression} = ${calc.result} (${calc.processingTime.toFixed(2)}ms)`
).join('\n')}

Statistics:
- Total Calculations: ${stats.calculationsPerformed}
- Average Processing Time: ${stats.averageTime.toFixed(2)}ms
- Errors Encountered: ${stats.errorsEncountered}
- Angle Mode: ${angleMode.toUpperCase()}
- Memory Value: ${memory}
`;

    const blob = new Blob([historyContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'calculator-history.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('History downloaded!');
  };

  const clearAll = (): void => {
    setDisplay('0');
    setExpression('');
    setMemory(0);
    setHistory([]);
    setStats({
      calculationsPerformed: 0,
      totalTime: 0,
      averageTime: 0,
      lastOperation: '',
      errorsEncountered: 0
    });
    showNotification('Calculator reset!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleCalculation = (): void => {
    setExpression('sin(30) + cos(60) * π');
    setDisplay('sin(30) + cos(60) * π');
    showNotification('Sample calculation loaded!');
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Scientific Calculator</h1>
              <p className="text-gray-300 text-sm">Advanced mathematical calculator with scientific functions</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Calculation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-indigo-400">{stats.calculationsPerformed}</div>
                <div className="text-gray-300 text-sm">Calculations</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.errorsEncountered}</div>
                <div className="text-gray-300 text-sm">Errors</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{angleMode.toUpperCase()}</div>
                <div className="text-gray-300 text-sm">Angle Mode</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{formatNumber(memory)}</div>
                <div className="text-gray-300 text-sm">Memory</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Display and Calculator */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 flex flex-col">
            {/* Display */}
            <div className="mb-4">
              <div className="bg-gray-900 rounded-lg p-4 mb-2">
                <div className="text-right">
                  <div className="text-sm text-gray-400 h-6 overflow-hidden">
                    {expression || ''}
                  </div>
                  <div className="text-3xl font-mono text-white break-all">
                    {display}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleButtonClick(angleMode === 'deg' ? 'RAD' : 'DEG')}
                    className={`px-3 py-1 rounded ${angleMode === 'deg' ? 'bg-indigo-600' : 'bg-purple-600'} text-white`}
                  >
                    {angleMode.toUpperCase()}
                  </button>
                  <div className="text-gray-400">
                    M: {formatNumber(memory)}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={copyToClipboard}
                    className="p-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={loadSampleCalculation}
                    className="p-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                  >
                    <Zap className="w-4 h-4" />
                  </button>
                  <button
                    onClick={clearAll}
                    className="p-1 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Calculator Buttons */}
            <div className="grid grid-cols-8 gap-2 flex-1">
              {/* Row 1 - Memory and Functions */}
              {['MC', 'MR', 'MS', 'M+', 'M-', '2nd', '(', ')'].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleButtonClick(btn)}
                  className="bg-gray-700 hover:bg-gray-600 text-white p-3 rounded transition-colors text-sm font-medium"
                >
                  {btn}
                </button>
              ))}

              {/* Row 2 - Scientific Functions */}
              {['sin', 'cos', 'tan', 'ln', 'log', 'x²', 'x³', 'xʸ'].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleButtonClick(btn)}
                  className="bg-indigo-700 hover:bg-indigo-600 text-white p-3 rounded transition-colors text-sm font-medium"
                >
                  {btn}
                </button>
              ))}

              {/* Row 3 - More Functions */}
              {['asin', 'acos', 'atan', 'π', 'e', '√', '1/x', '⌫'].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleButtonClick(btn)}
                  className="bg-purple-700 hover:bg-purple-600 text-white p-3 rounded transition-colors text-sm font-medium"
                >
                  {btn}
                </button>
              ))}

              {/* Row 4 - Numbers and Operations */}
              {['C', 'CE', '±', '÷', '7', '8', '9', '×'].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleButtonClick(btn)}
                  className={`p-3 rounded transition-colors font-medium ${
                    ['÷', '×', '-', '+', '='].includes(btn)
                      ? 'bg-orange-600 hover:bg-orange-500 text-white'
                      : ['C', 'CE', '±'].includes(btn)
                      ? 'bg-red-600 hover:bg-red-500 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-white'
                  }`}
                >
                  {btn}
                </button>
              ))}

              {/* Row 5 */}
              {['abs', 'exp', 'mod', '-', '4', '5', '6', '+'].map((btn) => (
                <button
                  key={btn}
                  onClick={() => handleButtonClick(btn)}
                  className={`p-3 rounded transition-colors font-medium ${
                    ['÷', '×', '-', '+', '='].includes(btn)
                      ? 'bg-orange-600 hover:bg-orange-500 text-white'
                      : ['abs', 'exp', 'mod'].includes(btn)
                      ? 'bg-indigo-700 hover:bg-indigo-600 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-white'
                  }`}
                >
                  {btn}
                </button>
              ))}

              {/* Row 6 */}
              {['floor', 'ceil', 'round', '=', '1', '2', '3', '='].map((btn, index) => (
                <button
                  key={`${btn}-${index}`}
                  onClick={() => handleButtonClick(btn)}
                  className={`p-3 rounded transition-colors font-medium ${
                    index === 7 ? 'row-span-2' : ''
                  } ${
                    btn === '='
                      ? 'bg-orange-600 hover:bg-orange-500 text-white'
                      : ['floor', 'ceil', 'round'].includes(btn)
                      ? 'bg-purple-700 hover:bg-purple-600 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-white'
                  }`}
                >
                  {btn}
                </button>
              ))}

              {/* Row 7 */}
              {['rand', 'fact', '%', '0', '0', '.', '.'].slice(0, 7).map((btn, index) => (
                <button
                  key={`${btn}-${index}`}
                  onClick={() => handleButtonClick(btn)}
                  className={`p-3 rounded transition-colors font-medium ${
                    index === 3 ? 'col-span-2' : ''
                  } ${
                    ['rand', 'fact', '%'].includes(btn)
                      ? 'bg-indigo-700 hover:bg-indigo-600 text-white'
                      : 'bg-gray-600 hover:bg-gray-500 text-white'
                  }`}
                >
                  {btn}
                </button>
              ))}
            </div>
          </div>

          {/* History and Downloads */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">History</h2>
              <button
                onClick={downloadHistory}
                disabled={history.length === 0}
                className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-2 overflow-y-auto min-h-[400px]">
              {history.length > 0 ? (
                <div className="space-y-2">
                  {history.slice(0, 10).map((calc, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded text-sm ${
                        calc.isError ? 'bg-red-900/50 border-l-2 border-red-500' : 'bg-gray-700'
                      }`}
                    >
                      <div className="font-mono text-xs text-gray-400 truncate">
                        {calc.expression}
                      </div>
                      <div className={`font-mono ${calc.isError ? 'text-red-400' : 'text-white'}`}>
                        = {calc.result}
                      </div>
                      <div className="text-xs text-gray-500">
                        {calc.timestamp.toLocaleTimeString()} ({calc.processingTime.toFixed(1)}ms)
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No calculations yet</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScientificCalculatorTool;